import React, { FC, FormEvent, useEffect, useState } from "react";
import {
  DatePicker,
  Label,
  MaskedTextField,
  mergeStyleSets,
  Stack,
  Modal,
  PrimaryButton,
  DefaultButton,
  IconButton,
  getTheme,
  MessageBar,
  MessageBarType
} from "@fluentui/react";
import { Patch } from "../../api/apiUtils";
import moment from "moment";
import { ErrorResponse, LineHandling, TruckNomination } from "../../models";
import { last6Digits } from "../../services/Helper";

type Props = {
  nomination: TruckNomination;
  onSuccess(value: string): void;
  onClose(): void;
};
const classes = mergeStyleSets({
  container: {
    padding: 35,
    width: 500
  },
  header: {
    display: "flex",
    flex: "1 1 auto"
  },
  footer: {
    marginTop: 35,
    display: "flex",
    justifyContent: "space-between"
  },
  title: {
    fontWeight: 500,
    fontSize: 20,
    lineHeight: 23,
    alignItems: "center",
    letterSpacing: "0.0015em",
    color: "#12427D"
  },
  buttons: {
    borderRadius: 2,
    height: 52,
    width: 210
  },
  inputField: {
    fontSize: "22px !important"
  },
  calendar: {
    fontSize: 15
  }
});
const theme = getTheme();
const iconButtonStyles = {
  root: {
    color: theme.palette.neutralPrimary,
    marginLeft: "auto",
    marginTop: "4px",
    marginRight: "2px"
  },
  rootHovered: {
    color: theme.palette.neutralDark
  }
};

const EtaModal: FC<Props> = (props: Props) => {
  const [value, setValue] = useState(new Date());
  const [timeValue, setTimeValue] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const saveEtaAsync = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isValid() && timeValue) {
      let hour = parseInt(timeValue.substr(0, 2));
      let minute = parseInt(timeValue.substr(3, 2));
      let etaVal = value;
      etaVal.setHours(hour);
      etaVal.setMinutes(minute);
      setIsLoading(true);

      const response = await Patch(
        `/api/nomination/${props.nomination.nominationNo}`,
        JSON.stringify([
          {
            op: "replace",
            path: "/eta",
            value: etaVal.toISOString()
          }
        ])
      );
      setIsLoading(false);
      if (response.ok) {
        const result = (await response.json()) as LineHandling;
        props.onSuccess(result.eta);
      } else {
        const result = (await response.json()) as ErrorResponse;
        setErrorMessage(result.message);
      }
    }
  };

  const isValid = (): boolean => {
    const timeEx = new RegExp(/^(?:2[0-3]|[01][0-9]):[0-5][0-9]$/);
    if (timeValue && timeEx.test(timeValue)) {
      return true;
    }

    setErrorMessage("Invalid Time value");
    return false;
  };

  useEffect(() => {
    if (props.nomination.eta) {
      const local = moment.utc(`${props.nomination.eta} UTC`).local();
      setValue(local.toDate());
      setTimeValue(local.format("HH:mm"));
    }
  }, [props.nomination]);

  return (
    <Modal
      titleAriaId="title"
      isOpen={true}
      onDismiss={props.onClose}
      isBlocking={false}
      containerClassName={classes.container}
    >
      <form onSubmit={saveEtaAsync}>
        <div className={classes.header}>
          <span
            id="lbl-title"
            className={classes.title}
          >{`Line Handling: #${last6Digits(
            props.nomination.nominationNo
          )}`}</span>
          <IconButton
            id="btn-close"
            styles={iconButtonStyles}
            iconProps={{ iconName: "Cancel" }}
            ariaLabel="Close popup modal"
            onClick={props.onClose}
            disabled={isLoading}
          />
        </div>
        <div>
          <Stack>
            {errorMessage && (
              <MessageBar
                messageBarType={MessageBarType.error}
                isMultiline={false}
                onDismiss={() => setErrorMessage(undefined)}
                dismissButtonAriaLabel="Close"
              >
                {errorMessage}
              </MessageBar>
            )}

            <Label>ETA</Label>
            <DatePicker
              id="txt-eta-date"
              label="Date"
              className={classes.inputField}
              formatDate={(val?: Date) => (val ? moment(val).format("ll") : "")}
              onSelectDate={(date: Date | null | undefined) =>
                date && setValue(date)
              }
              disabled={isLoading}
              value={value}
              minDate={new Date()}
            />
            <MaskedTextField
              id="txt-eta-time"
              label="Time"
              className={classes.inputField}
              mask="12:32"
              maskFormat={{
                "1": /[0-2]/,
                "2": /[0-9]/,
                "3": /[0-5]/
              }}
              disabled={isLoading}
              onChange={(e, value?: string) => setTimeValue(value)}
              value={timeValue}
            />
          </Stack>
        </div>
        <div className={classes.footer}>
          <PrimaryButton
            id="btn-submit"
            className={classes.buttons}
            disabled={isLoading}
            type="submit"
          >
            Submit
          </PrimaryButton>
          <DefaultButton
            id="btn-cancel"
            className={classes.buttons}
            onClick={props.onClose}
            disabled={isLoading}
          >
            Cancel
          </DefaultButton>
        </div>
      </form>
    </Modal>
  );
};

export default EtaModal;
